/**
 * PURPOSE: Renderizar un selector buscable de sedes reutilizable en Información Pública de Winscore.
 * RESPONSIBILITIES: Buscar sin tildes, seleccionar, limpiar y mostrar dirección de sedes disponibles.
 * COLLABORATORS: CardConfigSportInformation.jsx y TrainingEditor.jsx.
 * ROUTE: winscore/src/components/Cards/CardConfiguration/CardConfigSportInformation/SearchableVenueSelect.jsx
 */
import React, { useMemo, useState } from "react";
import { FaChevronDown, FaSearch, FaTimes } from "react-icons/fa";

const normalizeSearchValue = (value = "") =>
  String(value)
    .trim()
    .toLocaleLowerCase("es-PE")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export function SearchableVenueSelect({
  label,
  value,
  onChange,
  options,
  placeholder = "Buscar sede",
  disabled = false,
}) {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const selectedVenue = options.find((venue) => String(venue.id) === String(value));
  const normalizedQuery = normalizeSearchValue(query);

  const filteredOptions = useMemo(() => {
    if (!normalizedQuery) return options;

    return options.filter((venue) => {
      const haystack = normalizeSearchValue(
        [venue.name, venue.address].filter(Boolean).join(" "),
      );
      return haystack.includes(normalizedQuery);
    });
  }, [normalizedQuery, options]);

  const displayValue = isOpen || query ? query : selectedVenue?.name || "";

  const handleSelect = (venueId) => {
    onChange(String(venueId || ""));
    setQuery("");
    setIsOpen(false);
  };

  return (
    <label className="relative grid gap-2 text-sm font-semibold text-gray-700">
      {label ? <span>{label}</span> : null}
      <div className="relative">
        <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={displayValue}
          disabled={disabled}
          placeholder={selectedVenue?.name || placeholder}
          onFocus={() => {
            setIsOpen(true);
            setQuery("");
          }}
          onBlur={() => window.setTimeout(() => setIsOpen(false), 160)}
          onChange={(event) => {
            setQuery(event.target.value);
            setIsOpen(true);
          }}
          className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-16 font-normal focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
        />
        {value ? (
          <button
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => handleSelect("")}
            className="absolute right-9 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
            aria-label="Limpiar sede"
          >
            <FaTimes className="h-3 w-3" />
          </button>
        ) : null}
        <FaChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />

        {isOpen ? (
          <div className="absolute z-30 mt-2 max-h-64 w-full overflow-y-auto rounded-xl border border-gray-200 bg-white p-1 shadow-xl">
            {filteredOptions.length ? (
              filteredOptions.map((venue) => {
                const selected = String(venue.id) === String(value);
                return (
                  <button
                    type="button"
                    key={venue.id}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleSelect(venue.id)}
                    className={`grid w-full gap-0.5 rounded-lg px-3 py-2 text-left text-sm transition ${
                      selected
                        ? "bg-blue-50 text-blue-700"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <span className="font-semibold">{venue.name}</span>
                    {venue.address ? (
                      <span className="text-xs text-gray-400">{venue.address}</span>
                    ) : null}
                  </button>
                );
              })
            ) : (
              <div className="rounded-lg bg-gray-50 px-3 py-3 text-sm font-medium text-gray-500">
                No hay sedes que coincidan con tu búsqueda.
              </div>
            )}
          </div>
        ) : null}
      </div>
    </label>
  );
}
